import React, { useState } from 'react';
import styled from 'styled-components';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { adminTeamSlotSwapAPI } from '../services/adminApi';

const Panel = styled.div`
  padding: 1.5rem;
`;

const Title = styled.h2`
  font-size: 1.15rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 0.5rem 0;
`;

const Help = styled.p`
  font-size: 0.9rem;
  color: #6b7280;
  line-height: 1.55;
  margin: 0 0 1rem 0;
`;

const Warn = styled.div`
  padding: 0.9rem 1rem;
  border-radius: 8px;
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  border: 1px solid #f59e0b;
  color: #78350f;
  font-size: 0.875rem;
  margin-bottom: 1.25rem;
`;

const SetupGuide = styled.div`
  font-size: 0.875rem;
  color: #1e3a5f;
  line-height: 1.55;
  margin-bottom: 1.1rem;
  padding: 0.95rem 1.05rem;
  border-radius: 8px;
  background: #e0f2fe;
  border: 1px solid #7dd3fc;
`;

const SetupGuideOl = styled.ol`
  margin: 0.5rem 0 0;
  padding-left: 1.2rem;

  li {
    margin-bottom: 0.4rem;
  }
`;

const NavButtonRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.85rem;
`;

const NavLinkButton = styled.button`
  padding: 0.45rem 0.9rem;
  border: 1px solid #043663;
  border-radius: 6px;
  background: white;
  color: #043663;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  &:hover {
    background: #f0f9ff;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1rem;

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #374151;
`;

const Select = styled.select`
  padding: 0.6rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.875rem;
  background: white;
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.6rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.875rem;
  font-family: inherit;
  min-height: 4rem;
  margin-bottom: 1rem;
  box-sizing: border-box;
`;

const RowBtns = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
  margin-bottom: 1rem;
`;

const Button = styled.button`
  padding: 0.55rem 1.1rem;
  background: #043663;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  &:hover {
    background: #032a4f;
  }
  &:disabled {
    background: #9ca3af;
    cursor: not-allowed;
  }
`;

const ButtonSecondary = styled(Button)`
  background: #4b5563;
  &:hover {
    background: #374151;
  }
`;

const ReviewCard = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 1rem 1.1rem;
  margin-bottom: 1.25rem;
  background: #fafafa;
`;

const ReviewHeadline = styled.h3`
  font-size: 1rem;
  font-weight: 700;
  color: #111827;
  margin: 0 0 0.75rem 0;
`;

const CheckUl = styled.ul`
  margin: 0 0 1rem 1.15rem;
  padding: 0;
  font-size: 0.875rem;
  color: #374151;
  line-height: 1.5;

  li {
    margin-bottom: 0.35rem;
  }
`;

const FixturesTableWrap = styled.div`
  overflow-x: auto;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: white;
`;

const FixturesTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.8125rem;
`;

const Th = styled.th`
  text-align: left;
  padding: 0.6rem 0.75rem;
  background: #f3f4f6;
  color: #374151;
  font-weight: 600;
  white-space: nowrap;
`;

const Td = styled.td`
  padding: 0.55rem 0.75rem;
  border-top: 1px solid #f3f4f6;
  color: #1f2937;
`;

const ConfirmRow = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: #374151;
  margin-bottom: 1rem;
  cursor: pointer;
  input {
    margin-top: 0.2rem;
  }
`;

const AuditsSection = styled.div`
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid #e5e7eb;
`;

const AuditsHead = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
`;

const AuditsTitle = styled.h3`
  font-size: 1rem;
  font-weight: 700;
  margin: 0;
  color: #1f2937;
`;

const Meta = styled.span`
  font-size: 0.8rem;
  color: #6b7280;
`;

const TechToggle = styled.button`
  background: none;
  border: none;
  color: #2563eb;
  font-size: 0.8rem;
  cursor: pointer;
  padding: 0.25rem 0;
  text-decoration: underline;
  margin-bottom: 0.5rem;
`;

const TechPre = styled.pre`
  margin: 0;
  padding: 0.75rem;
  background: #1f2937;
  color: #e5e7eb;
  border-radius: 6px;
  font-size: 0.72rem;
  overflow: auto;
  max-height: 16rem;
`;

function unwrap(res) {
  if (res && typeof res === 'object' && 'data' in res && res.success !== false) return res.data;
  return res;
}

/**
 * @param {{
 *   seasonId: string,
 *   readOnly?: boolean,
 *   onGoToTeams?: () => void,
 *   onGoToPlayers?: () => void,
 * }} props
 */
const AdminTeamSlotSwapPanel = ({ seasonId, readOnly = false, onGoToTeams, onGoToPlayers }) => {
  const queryClient = useQueryClient();
  const [outgoingTeamId, setOutgoingTeamId] = useState('');
  const [incomingTeamId, setIncomingTeamId] = useState('');
  const [reason, setReason] = useState('');
  const [previewPayload, setPreviewPayload] = useState(null);
  const [auditScope, setAuditScope] = useState(true);
  const [confirmed, setConfirmed] = useState(false);
  const [showTechnical, setShowTechnical] = useState(false);

  const { data: outgoingRes, isLoading: outLoading } = useQuery(
    ['team-slot-outgoing', seasonId],
    async () => adminTeamSlotSwapAPI.outgoingCandidates(seasonId),
    { enabled: !!seasonId, retry: false }
  );

  const outgoingRows = unwrap(outgoingRes) || [];

  const { data: incomingRes, isLoading: incLoading } = useQuery(
    ['team-slot-incoming', seasonId, outgoingTeamId],
    async () =>
      adminTeamSlotSwapAPI.incomingCandidates(seasonId, {
        excludeTeamId: outgoingTeamId || undefined,
      }),
    { enabled: !!seasonId && !!outgoingTeamId, retry: false }
  );

  const incomingRows = unwrap(incomingRes) || [];

  const auditSeasonParam = auditScope ? seasonId : undefined;
  const { data: auditsRes, refetch: refetchAudits } = useQuery(
    ['team-slot-swap-audits', tenantKey(auditSeasonParam)],
    async () =>
      adminTeamSlotSwapAPI.listAudits({
        ...(auditSeasonParam ? { seasonId: auditSeasonParam } : {}),
        limit: 50,
      }),
    { enabled: !!seasonId, retry: false }
  );

  const auditsBundle = unwrap(auditsRes);
  const audits = auditsBundle?.rows || [];
  const auditTotal = auditsBundle?.total ?? 0;

  const previewMutation = useMutation({
    mutationFn: () =>
      adminTeamSlotSwapAPI.preview(seasonId, {
        outgoingTeamId,
        incomingTeamId,
      }),
    onSuccess: (res) => {
      const data = unwrap(res);
      setPreviewPayload(data || null);
      setConfirmed(false);
      toast.success('Preview ready — review fixtures and checklist before executing.');
    },
    onError: (err) => {
      const msg = err.response?.data?.message || err.message || 'Preview failed';
      setPreviewPayload(null);
      toast.error(msg);
    },
  });

  const executeMutation = useMutation({
    mutationFn: () =>
      adminTeamSlotSwapAPI.execute(seasonId, {
        outgoingTeamId,
        incomingTeamId,
        reason: reason.trim() || undefined,
      }),
    onSuccess: (res) => {
      const data = unwrap(res);
      setConfirmed(false);
      setPreviewPayload(null);
      toast.success(
        data?.audit_id
          ? `Swap completed (audit ${String(data.audit_id).slice(0, 8)}…)`
          : 'Swap completed.'
      );
      queryClient.invalidateQueries(['team-slot-swap-audits']);
      queryClient.invalidateQueries(['team-slot-outgoing', seasonId]);
      queryClient.invalidateQueries(['team-slot-incoming', seasonId, outgoingTeamId]);
      refetchAudits();
    },
    onError: (err) => {
      const msg = err.response?.data?.message || err.message || 'Swap failed';
      toast.error(msg);
    },
  });

  const review = previewPayload?.review;
  const canPreview = !!seasonId && !!outgoingTeamId && !!incomingTeamId && outgoingTeamId !== incomingTeamId;
  const fixtures = review?.fixtures || [];
  const staleTeamIds =
    !!previewPayload &&
    !!review &&
    (review.teams?.outgoing?.id !== outgoingTeamId || review.teams?.incoming?.id !== incomingTeamId);

  const executeOk =
    canPreview &&
    previewPayload &&
    review &&
    confirmed &&
    !readOnly &&
    !executeMutation.isLoading &&
    !staleTeamIds;

  return (
    <Panel>
      <Title>Mid-phase vacant slot swap</Title>
      <Help>
        The <strong>leaving</strong> side is any team with fixtures in this phase (including teams whose registration was
        already turned off when they left). The <strong>replacement</strong> can be brand new: zero past phases or
        registrations — it must simply have no fixtures in this phase yet.
        Fixtures stay with the slot; opponents are unchanged. Chat, scheduling drafts, votes, and score confirmations for
        those matches will be cleared. Deploy migration{' '}
        <code style={{ fontSize: '0.85em' }}>064_team_slot_replacement.sql</code> before executing.
      </Help>

      <SetupGuide>
        <strong>Replacement team &amp; players — where that happens in admin</strong>
        <div style={{ marginTop: '0.35rem' }}>
          This screen only <em>reassigns fixtures</em>. The <strong>replacement</strong> side must already exist as a
          team record with <strong>no</strong> matches in this phase. Creating the team roster is not done here.
        </div>
        <SetupGuideOl>
          <li>
            <strong>Normal path:</strong> Players complete <strong>season / registration checkout</strong>{' '}
            (Settings → Tournaments / seasons). That flow creates the team, login, and the two roster players when
            payment succeeds — same as any new league signup.
          </li>
          <li>
            <strong>Admin → Teams:</strong> Find the team, <strong>View</strong> for members. You can use{' '}
            <strong>Activate in phase</strong> on a dormant shell if you only need a registration row — but do{' '}
            <strong>not</strong> generate or place them into fixtures for this phase until after the swap if that would
            create <code style={{ fontSize: '0.8em' }}>league_matches</code> rows for them (incoming must have none).
          </li>
          <li>
            Use sidebar <strong>Team &amp; player setup</strong> to create player logins, shell teams, and roster links outside
            checkout when needed.
          </li>
          {onGoToPlayers ? (
            <li>
              <strong>Country tenant:</strong> Sidebar <strong>Players</strong> lists registered people; league teams
              are still managed under <strong>Teams</strong>.
            </li>
          ) : null}
        </SetupGuideOl>
        {(onGoToTeams || onGoToPlayers) && (
          <NavButtonRow>
            {onGoToTeams ? (
              <NavLinkButton type="button" onClick={onGoToTeams}>
                Go to Teams (roster &amp; phase actions)
              </NavLinkButton>
            ) : null}
            {onGoToPlayers ? (
              <NavLinkButton type="button" onClick={onGoToPlayers}>
                Go to Players (country list)
              </NavLinkButton>
            ) : null}
          </NavButtonRow>
        )}
      </SetupGuide>

      {readOnly && (
        <Warn>
          This phase is completed — swaps cannot be executed here. You can still preview to verify fixture mapping, and
          use the audit log below.
        </Warn>
      )}

      {!readOnly && (
        <Warn>
          Always run <strong>Preview</strong> first and read every row in the fixtures table. Executing cannot be undone
          from the UI.
        </Warn>
      )}

      <Grid>
        <Field>
          Leaving team (fixtures in this phase — registration may already be inactive)
          <Select
            value={outgoingTeamId}
            onChange={(e) => {
              setOutgoingTeamId(e.target.value);
              setIncomingTeamId('');
              setPreviewPayload(null);
              setConfirmed(false);
            }}
            disabled={outLoading || !seasonId}
          >
            <option value="">{outLoading ? 'Loading…' : 'Select…'}</option>
            {(outgoingRows || []).map((r) => (
              <option key={r.id} value={r.id}>
                {r.display_name || r.id}
              </option>
            ))}
          </Select>
        </Field>
        <Field>
          Replacement team (no fixtures in this phase)
          <Select
            value={incomingTeamId}
            onChange={(e) => {
              setIncomingTeamId(e.target.value);
              setPreviewPayload(null);
              setConfirmed(false);
            }}
            disabled={incLoading || !outgoingTeamId}
          >
            <option value="">Select leaving team first…</option>
            {(incomingRows || []).map((r) => (
              <option key={r.id} value={r.id}>
                {r.display_name || r.id}
              </option>
            ))}
          </Select>
        </Field>
      </Grid>

      <Field as="span" style={{ marginBottom: '0.35rem' }}>
        Reason / notes (optional, stored in audit)
      </Field>
      <TextArea value={reason} onChange={(e) => setReason(e.target.value)} disabled={readOnly} />

      <RowBtns>
        <Button
          type="button"
          disabled={!canPreview || previewMutation.isLoading}
          onClick={() => previewMutation.mutate()}
        >
          {previewMutation.isLoading ? 'Preview…' : 'Preview swap'}
        </Button>
        <ButtonSecondary
          type="button"
          disabled={!executeOk || executeMutation.isLoading}
          onClick={() => {
            window.alert(
              'This replaces the leaving team’s slot for this phase only. Match artefacts for affected fixtures will be cleared. Confirm you have verified the fixture list above.'
            );
            executeMutation.mutate();
          }}
        >
          {executeMutation.isLoading ? 'Executing…' : 'Execute swap'}
        </ButtonSecondary>
      </RowBtns>

      {staleTeamIds && (
        <Warn style={{ background: '#fee2e2', borderColor: '#f87171', color: '#7f1d1d' }}>
          Team selection changed since this preview — run <strong>Preview swap</strong> again before executing.
        </Warn>
      )}

      {review && (
        <ReviewCard>
          <ReviewHeadline>{review.headline}</ReviewHeadline>
          <Meta style={{ display: 'block', marginBottom: '0.75rem' }}>
            {review.season?.name} ({review.season?.status}) — {review.division?.name}
          </Meta>
          <CheckUl>
            {(review.checklist || []).map((line, i) => (
              <li key={i}>{line}</li>
            ))}
          </CheckUl>

          <div style={{ fontSize: '0.8125rem', fontWeight: 600, color: '#374151', marginBottom: '0.5rem' }}>
            Fixtures ({fixtures.length})
          </div>
          <FixturesTableWrap>
            <FixturesTable>
              <thead>
                <tr>
                  <Th>#</Th>
                  <Th>Match</Th>
                  <Th>Opponent</Th>
                  <Th>Status</Th>
                  <Th>Incoming will be home</Th>
                </tr>
              </thead>
              <tbody>
                {fixtures.map((f) => (
                  <tr key={f.match_id}>
                    <Td>{f.match_number}</Td>
                    <Td title={f.match_id} style={{ fontFamily: 'ui-monospace, monospace', fontSize: '0.72rem' }}>
                      {String(f.match_id).slice(0, 8)}…
                    </Td>
                    <Td>{f.opponent_name}</Td>
                    <Td>{f.status}</Td>
                    <Td>{f.incoming_will_be_home ? 'Yes' : 'No'}</Td>
                  </tr>
                ))}
              </tbody>
            </FixturesTable>
          </FixturesTableWrap>

          {!!review.confirm_strings?.length && !readOnly ? (
            <ConfirmRow>
              <input
                type="checkbox"
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
                disabled={staleTeamIds}
              />
              <span>
                I confirm the swap from <strong>{review.teams?.outgoing?.label || '—'}</strong> to{' '}
                <strong>{review.teams?.incoming?.label || '—'}</strong> for exactly{' '}
                <strong>{fixtures.length}</strong> fixtures in this division — including home/away as shown.
              </span>
            </ConfirmRow>
          ) : null}
        </ReviewCard>
      )}

      {previewPayload && (
        <>
          <TechToggle type="button" onClick={() => setShowTechnical((x) => !x)}>
            {showTechnical ? 'Hide' : 'Show'} technical response (IDs, artefact flags)
          </TechToggle>
          {showTechnical && <TechPre>{JSON.stringify(previewPayload, null, 2)}</TechPre>}
        </>
      )}

      <AuditsSection>
        <AuditsHead>
          <AuditsTitle>Audit log</AuditsTitle>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem', color: '#4b5563' }}>
            <input
              type="checkbox"
              checked={auditScope}
              onChange={(e) => setAuditScope(e.target.checked)}
            />
            Only this phase
          </label>
        </AuditsHead>
        <Help style={{ marginTop: '-0.25rem' }}>
          Showing {audits.length} of {auditTotal} row(s){auditScope ? ' for the selected phase' : ' (all phases)'}.
        </Help>

        <FixturesTableWrap>
          <FixturesTable>
            <thead>
              <tr>
                <Th>When</Th>
                <Th>Leaving → Replacement</Th>
                <Th>Division</Th>
                <Th>Matches</Th>
                <Th>Admin</Th>
                <Th>Reason</Th>
              </tr>
            </thead>
            <tbody>
              {audits.length === 0 ? (
                <tr>
                  <Td colSpan={6} style={{ color: '#6b7280' }}>
                    No audit entries yet.
                  </Td>
                </tr>
              ) : (
                audits.map((a) => (
                  <tr key={a.id}>
                    <Td style={{ whiteSpace: 'nowrap', fontSize: '0.78rem' }}>
                      {formatWhen(a.created_at)}
                    </Td>
                    <Td>
                      {(a.outgoing_team_name || '—')} → {(a.incoming_team_name || '—')}
                    </Td>
                    <Td>{a.division_name || '—'}</Td>
                    <Td>{a.match_count ?? 0}</Td>
                    <Td style={{ fontSize: '0.78rem' }}>{a.admin_email || '—'}</Td>
                    <Td
                      style={{ fontSize: '0.75rem', maxWidth: '140px', overflow: 'hidden', textOverflow: 'ellipsis' }}
                      title={a.reason || ''}
                    >
                      {a.reason ? (a.reason.length > 48 ? `${a.reason.slice(0, 48)}…` : a.reason) : '—'}
                    </Td>
                  </tr>
                ))
              )}
            </tbody>
          </FixturesTable>
        </FixturesTableWrap>
      </AuditsSection>
    </Panel>
  );
};

function tenantKey(seasonOrNull) {
  return seasonOrNull || '_all';
}

function formatWhen(iso) {
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: 'short',
      timeStyle: 'short',
    });
  } catch {
    return iso || '';
  }
}

export default AdminTeamSlotSwapPanel;
